/**
 * v5/components/DetailRightPanel.tsx
 *
 * A diverged copy of `components/layout/DetailRightPanel.tsx`. Six files use this one,
 * including the routed CRM pipeline page and the routed messages page, so it needs the
 * same off-canvas branch or those pages get none of the narrow-width work.
 *
 * Inside a sheet, `defaultCollapsed`, `width`, `expandTrigger` and the collapse chevron
 * are **deliberate no-ops**, and the AI sidebar is ignored: the sheet owns visibility,
 * and the assistant force-collapsing a panel the sheet is showing would give it two
 * masters. See the live copy's docblock for the full reasoning.
 *
 * **Nothing keeps these two files in parity.** This one lacks the live copy's
 * `toggleTrigger` prop and wraps its children differently; both remain true after this
 * change.
 */
import { CollapseChevron } from "@/v5/components/ui/collapse-chevron";
import { cn } from "@/v5/lib/utils";
import { useAISidebarStore } from "@/store/aiSidebarStore";
import { useEffect, useRef, useState } from "react";
import { useInOffCanvas } from "@/components/layout/OffCanvasPanel";

interface DetailRightPanelProps {
  children: React.ReactNode;
  defaultCollapsed?: boolean;
  width?: string;
  expandTrigger?: number;
}

export function DetailRightPanel({
  children,
  defaultCollapsed = false,
  width = "w-[25rem]",
  expandTrigger,
}: DetailRightPanelProps) {
  const aiSidebarOpen = useAISidebarStore((s) => s.isOpen);
  const closeSidebar = useAISidebarStore((s) => s.closeSidebar);
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  const prevTrigger = useRef(expandTrigger);
  const inOffCanvas = useInOffCanvas();

  useEffect(() => {
    if (inOffCanvas) return;
    if (expandTrigger !== undefined && expandTrigger !== prevTrigger.current) {
      setCollapsed(false);
      closeSidebar();
      prevTrigger.current = expandTrigger;
    }
  }, [closeSidebar, expandTrigger, inOffCanvas]);

  useEffect(() => {
    if (inOffCanvas) return;
    if (aiSidebarOpen) {
      setCollapsed(true);
    }
  }, [aiSidebarOpen, inOffCanvas]);

  if (inOffCanvas) {
    return <div className="flex h-full min-h-0 w-full flex-col bg-white">{children}</div>;
  }

  return (
    <div className="relative z-50 flex-shrink-0">
      <CollapseChevron
        collapsed={collapsed}
        onClick={() => {
          setCollapsed((c) => {
            if (c) closeSidebar();
            return !c;
          });
        }}
        side="right"
        title={collapsed ? "Expand panel" : "Collapse panel"}
        className="z-40"
      />
      <div
        className={cn(
          "h-full transition-[width] duration-200 ease-linear overflow-hidden",
          collapsed ? "w-4 border-l-0" : `border-l border-border ${width}`,
        )}
      >
        {!collapsed && <div className={cn("h-full", width)}>{children}</div>}
      </div>
    </div>
  );
}
